/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

import { Typography, Button, DatePicker, Input, Card, Tag, Image } from "antd";
import { useAppDispatch, useAppSelector } from "../../../config/hooks";
import { processLatLngs, removeLatLngs } from "../ducks/actions";
import {
  CalendarIcon,
  FilterIcon,
  SearchIcon,
} from "../../../atoms/CustomIcons";
import DateNavigatorLeft from "../../../atoms/CustomIcons/DateNavigatorLeft";
import DateNavigatorRight from "../../../atoms/CustomIcons/DateNavigatorRight";
import RenderEventContent from "../Component/RenderEventContent";
import CustomDayHeader from "../Component/CustomDayHeader";
import FullPageLayout from "../../../templates/FullPageLayout";
import Header from "./Header";
import SortingIcon from "../../../atoms/CustomIcons/SortingIcon";
import {
  DUMMY_DATA,
  INITIAL_UNSCHEDULED_TASKS_DATA,
} from "../dummyData-schedule";
import EmptyCard from "../../../atoms/EmptyCard";

dayjs.extend(duration);
dayjs.extend(isSameOrAfter);

interface Coordinates {
  lat: number;
  lng: number;
}

interface DriveTimeDataApiItem {
  from: string;
  to: string;
  duration_seconds: number;
  duration_text: string;
}

interface VisitExtendedProps {
  type: "visit" | "drive";
  durationMinutes?: number;
  driveTimeMinutes?: number;
  location?: string;
  nextVisitLocation?: string;
  backgroundColor?: string;
  textColor?: string;
  borderColor?: string;
}

interface FullCalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  date?: string;
  allDay?: boolean;
  editable?: boolean;
  origin?: Coordinates;
  extendedProps?: VisitExtendedProps;
}

const generateEventsWithDriveTimes = async (
  initialVisitEvents: FullCalendarEvent[],
  driveTimeData: DriveTimeDataApiItem[]
): Promise<FullCalendarEvent[]> => {
  const processedEvents: FullCalendarEvent[] = [];

  const sortedVisitEvents = [...initialVisitEvents].sort((a, b) =>
    dayjs(a.start).diff(dayjs(b.start))
  );

  let currentEffectiveEndTime: dayjs.Dayjs | null = null;

  for (let i = 0; i < sortedVisitEvents.length; i++) {
    const currentVisit = { ...sortedVisitEvents[i] };
    if (
      currentEffectiveEndTime &&
      dayjs(currentVisit.start).isBefore(currentEffectiveEndTime)
    ) {
      const originalDuration = dayjs(currentVisit.end).diff(
        dayjs(currentVisit.start)
      );
      currentVisit.start = currentEffectiveEndTime.toISOString();
      currentVisit.end = dayjs(currentEffectiveEndTime)
        .add(originalDuration, "millisecond")
        .toISOString();
    }

    processedEvents.push(currentVisit);
    currentEffectiveEndTime = dayjs(currentVisit.end);

    if (i < sortedVisitEvents.length - 1) {
      const nextVisit = sortedVisitEvents[i + 1];

      const isSameCalendarDay = dayjs(currentVisit.end).isSame(
        dayjs(nextVisit.start),
        "day"
      );

      if (currentVisit.origin && nextVisit.origin && isSameCalendarDay) {
        const fromCoordString = `${currentVisit.origin.lat},${currentVisit.origin.lng}`;
        const toCoordString = `${nextVisit.origin.lat},${nextVisit.origin.lng}`;

        const foundDriveTime = driveTimeData.find(
          (data) => data.from === fromCoordString && data.to === toCoordString
        );

        if (foundDriveTime && foundDriveTime.duration_seconds > 0) {
          const driveStart = currentEffectiveEndTime;
          const duration = foundDriveTime.duration_seconds + 600;
          // if (foundDriveTime.duration_seconds < 500) {
          //   duration = foundDriveTime.duration_seconds + 500;
          // } else {
          //   duration = foundDriveTime.duration_seconds;
          // }
          const driveEnd = driveStart.add(duration, "second");
          processedEvents.push({
            id: `drive-${currentVisit.id}-to-${nextVisit.id}`,
            title: `Drive to ${
              nextVisit.extendedProps?.location || "next location"
            }`,
            start: driveStart.toISOString(),
            end: driveEnd.toISOString(),
            editable: false,
            extendedProps: {
              type: "drive",
              driveTimeMinutes: Math.ceil(duration / 60),
              location: currentVisit.extendedProps?.location,
              nextVisitLocation: nextVisit.extendedProps?.location,
              backgroundColor: "#E3F2FD",
              borderColor: "#2196F3",
              textColor: "#0D47A1",
            },
          });
          currentEffectiveEndTime = driveEnd;
        }
      }
    }
  }
  return processedEvents;
};

export default function ScheduleWork() {
  const [hoursData, setHoursData] = useState<HourData[] | null>(null);
  const [crewData, setCrewData] = useState<any[]>();
  const calendarRef = useRef<any>(null);
  const location = window.location.pathname;
  const pathSegments = location.split("/");
  const companyId = pathSegments[pathSegments.length - 1];
  console.log("companyId", companyId);
  const [selectedCrewId, setSelectedCrewId] = useState<string | undefined>(
    undefined
  );
  const [visitEvents, setVisitEvents] = useState<FullCalendarEvent[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<FullCalendarEvent[]>([]);
  const [currentWeekRange, setCurrentWeekRange] = useState("");
  const [currentWeekStartEnd, setCurrentWeekStartEnd] = useState({
    start: dayjs().startOf("week").format("YYYY-MM-DD"),
    end: dayjs().endOf("week").format("YYYY-MM-DD"),
  });

  const [unscheduledTasks, setUnscheduledTasks] = useState<any[]>(
    INITIAL_UNSCHEDULED_TASKS_DATA
  );

  useEffect(() => {
    if (DUMMY_DATA) {
      const transformedCrewData: any[] = [];

      DUMMY_DATA.forEach((daySchedule) => {
        const date = daySchedule.date; // Get the date for the current day's schedule

        daySchedule.serviceLines.forEach((serviceLine) => {
          serviceLine.managers.forEach((manager) => {
            manager.crew.forEach((crew) => {
              // Find if this crew already exists in our transformed data
              const existingCrew: any = transformedCrewData.find(
                (c) => c.id === crew.id
              );

              // Map visits to ensure their 'start' and 'end' times include the correct date
              const visitsForThisDate = crew.visit.map((visit) => {
                // Parse the time part from the existing visit.start/end
                const visitStartTime = dayjs(visit.startTime, "h:mm A");
                const visitEndTime = dayjs(visit.endTime, "h:mm A");

                // Set the hour, minute, second on the specific date
                const startDateTime = dayjs(date)
                  .set("hour", visitStartTime.hour())
                  .set("minute", visitStartTime.minute())
                  .set("second", visitStartTime.second())
                  .format();

                const endDateTime = dayjs(date)
                  .set("hour", visitEndTime.hour())
                  .set("minute", visitEndTime.minute())
                  .set("second", visitEndTime.second())
                  .format();

                return {
                  ...visit,
                  start: startDateTime,
                  end: endDateTime,
                  // Add other properties as needed for FullCalendarEvent and extendedProps
                  editable: true,
                  extendedProps: {
                    type: "visit",
                    location: visit.address,
                    backgroundColor: "#E8F5E9",
                    borderColor: "#4CAF50",
                    textColor: "#1B5E20",
                  },
                };
              });

              // Add dummy crew members if the crew is new
              const crewMembers = existingCrew
                ? existingCrew.crewMembers
                : [
                    {
                      id: "member-001",
                      name: "Jake Ryan",
                      image: "/images/dummy/crew1.png",
                      role: "5678, Crew leader",
                      punchedIn: "6:38 AM",
                      crewLeader: true,
                      crewLeaderId: "crew-a-001",
                    },
                    {
                      id: "member-002",
                      name: "Carlos Eduardo",
                      image: "/images/dummy/crew2.png",
                      latePunchedIn: "7:59 AM",
                      role: "5679",
                      crewLeaderId: "crew-a-002",
                    },
                    {
                      id: "member-003",
                      name: "Tyler James",
                      image: "/images/dummy/crew3.png",
                      punchedIn: "7:25 AM",
                      role: "1265",
                      crewLeaderId: "crew-a-003",
                    },
                    {
                      id: "member-004",
                      name: "Mateo Luis",
                      image: "/images/dummy/crew4.png",
                      role: "2904",
                      crewLeaderId: "crew-a-004",
                    },
                  ];

              if (existingCrew) {
                // If the crew exists, append the visits for the current date
                existingCrew.visit.push(...visitsForThisDate);
              } else {
                // If the crew does not exist, create a new entry
                transformedCrewData.push({
                  id: crew.id,
                  name: crew.name,
                  crew_leader: crew.crew_leader,
                  hours: crew.hours,
                  crewMembers: crewMembers, // Add dummy crew members
                  visit: visitsForThisDate,
                });
              }
            });
          });
        });
      });
      setCrewData(transformedCrewData);
    }
  }, []);

  const handleDatesSet = (arg: any) => {
    const start = dayjs(arg.start);
    const end = dayjs(arg.end).subtract(1, "day"); // FullCalendar includes the next day for week view

    const formatted =
      start.format("MMM D") +
      " – " +
      (start.month() === end.month()
        ? end.format("MMM D")
        : end.format("MMM D"));

    setCurrentWeekStartEnd({
      start: start.format("YYYY-MM-DD"),
      end: end.format("YYYY-MM-DD"),
    });

    setCurrentWeekRange(formatted);
  };

  const dispatch = useAppDispatch();
  const driveTimeData = useAppSelector(
    (state) => state?.schedule?.driveTimeData?.data || []
  );
  const driveTimeLoader = useAppSelector(
    (state) => state?.schedule?.driveTimeData?.loading || false
  );
  const [isAutoScheduling, setIsAutoScheduling] = useState<boolean>(false);

  useEffect(() => {
    if (crewData && crewData.length > 0) {
      if (selectedCrewId === undefined) {
        setSelectedCrewId(crewData?.[0].id);
      }

      const currentCrew = crewData.find(
        (crew: any) => crew.id === (selectedCrewId || crewData?.[0].id)
      );

      if (currentCrew) {
        const mappedVisits: FullCalendarEvent[] = currentCrew.visit.map(
          (visit: any) => ({
            id: visit.id,
            title: visit.title,
            start: visit.start,
            end: visit.end,
            origin: visit.origin,
            editable: true,
            extendedProps: {
              type: "visit",
              location: visit.address,
              backgroundColor: "#E8F5E9",
              borderColor: "#4CAF50",
              textColor: "#1B5E20",
            },
            workOrders: visit?.workOrders || [],
            homeIcon: visit?.homeIcon || "",
          })
        );
        setVisitEvents(mappedVisits);
      } else {
        setVisitEvents([]);
      }
    } else {
      setSelectedCrewId(undefined);
      setVisitEvents([]);
    }
  }, [crewData, selectedCrewId]);

  const handleEventReceive = useCallback(
    (info: any) => {
      const taskId = info.event.id;
      const droppedDate = info.event.start; // The start time where it was dropped

      info.event.remove(); // Remove the temporary event created by FullCalendar's droppable

      const task = unscheduledTasks.find((t) => t.id === taskId);
      if (!task || !droppedDate) return;

      const start = dayjs(droppedDate);
      const end = start.add(task.durationHours, "hour");

      const newEvent: FullCalendarEvent = {
        id: `${task.id}-scheduled-${Date.now()}`, // Generate a unique ID for the new event instance
        title: task.title,
        start: start.toISOString(),
        end: end.toISOString(),
        origin: task.origin,
        editable: true,
        extendedProps: {
          ...task.extendedProps,
        },
      };

      setVisitEvents((prev) =>
        [...prev, newEvent].sort((a, b) => dayjs(a.start).diff(dayjs(b.start)))
      );

      setUnscheduledTasks((prev) => prev.filter((t) => t.id !== taskId));
    },
    [unscheduledTasks]
  );

  const triggerProcessLatLngs = useCallback(() => {
    dispatch(removeLatLngs());
    if (visitEvents.length > 0) {
      const coordinatesToProcess: Coordinates[] = visitEvents
        .filter((event) => event.origin !== undefined)
        .sort((a, b) => dayjs(a.start).diff(dayjs(b.start)))
        .map((event) => {
          if (event.origin === undefined) {
            throw new Error("Event origin is undefined");
          }
          return event.origin;
        });

      const mappedCoordinates = coordinatesToProcess.map((coord) => ({
        lat: coord.lat,
        lng: coord.lng,
      }));

      if (mappedCoordinates.length > 1) {
        const payload = {
          coordinates: mappedCoordinates,
        };
        dispatch(processLatLngs(payload));
      } else {
        console.log(
          "Not enough coordinates to calculate drive times for current crew."
        );

        setCalendarEvents(visitEvents);
      }
    }
  }, [dispatch, visitEvents]);

  useEffect(() => {
    triggerProcessLatLngs();
  }, [triggerProcessLatLngs]);

  interface HourData {
    date: string;
    hours: number; // Formatted as "Xh Ym"
  }

  const calculateDailyHours = (events: FullCalendarEvent[]): HourData[] => {
    const dailyTotalMinutes: { [date: string]: number } = {};

    events.forEach((event) => {
      // Ensure event has valid start and end times
      if (event.start && event.end) {
        const start = dayjs(event.start);
        const end = dayjs(event.end);

        // Only consider events that have a valid duration and are not "drive" events
        // If you want to include drive times in the total, remove the `&& event.extendedProps?.type !== "drive"` condition
        if (start.isValid() && end.isValid() && start.isBefore(end)) {
          const eventDurationMinutes = end.diff(start, "minute");
          const eventDate = start.format("YYYY-MM-DD"); // Get date in YYYY-MM-DD format

          // Add duration to the total for that day
          dailyTotalMinutes[eventDate] =
            (dailyTotalMinutes[eventDate] || 0) + eventDurationMinutes;
        }
      }
    });

    // Convert the daily total minutes into the desired 'hourData' format
    const hourData: HourData[] = Object.keys(dailyTotalMinutes).map((date) => {
      const totalMinutes = dailyTotalMinutes[date];

      return {
        date: date,
        hours: totalMinutes, // Trim to remove leading/trailing spaces if only one part exists
      };
    });

    // Sort the results by date
    return hourData.sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));
  };

  useMemo(() => {
    if (Array.isArray(driveTimeData) && visitEvents?.length > 0) {
      const generateAndSetCalendarEvents = async () => {
        const generated = await generateEventsWithDriveTimes(
          visitEvents,
          driveTimeData as DriveTimeDataApiItem[]
        );

        const dailyHours = calculateDailyHours(generated);

        setCalendarEvents(generated);
        setHoursData(dailyHours);
      };
      generateAndSetCalendarEvents();
    }
  }, [visitEvents, driveTimeData]);

  // const getNextValidTimeSlot = (
  //   currentTime: Dayjs,
  //   slotMinTimeStr: string,
  //   slotMaxTimeStr: string,
  //   slotDurationMinutes: number
  // ): Dayjs => {
  //   let nextTime = dayjs(currentTime).second(0).millisecond(0);

  //   const [minHour, minMinute] = slotMinTimeStr.split(":").map(Number);
  //   const [maxHour, maxMinute] = slotMaxTimeStr.split(":").map(Number);

  //   const minutes = nextTime.minute();
  //   if (minutes % slotDurationMinutes !== 0) {
  //     nextTime = nextTime.add(
  //       slotDurationMinutes - (minutes % slotDurationMinutes),
  //       "minute"
  //     );
  //   }

  //   const calendarDayMin = nextTime.hour(minHour).minute(minMinute);
  //   if (nextTime.isBefore(calendarDayMin)) {
  //     nextTime = calendarDayMin;
  //   }

  //   const calendarDayMaxBoundary = nextTime.hour(maxHour).minute(maxMinute);

  //   if (nextTime.isSameOrAfter(calendarDayMaxBoundary)) {
  //     nextTime = nextTime.add(1, "day").hour(minHour).minute(minMinute);
  //     const newMinutes = nextTime.minute();
  //     if (newMinutes % slotDurationMinutes !== 0) {
  //       nextTime = nextTime.add(
  //         slotDurationMinutes - (newMinutes % slotDurationMinutes),
  //         "minute"
  //       );
  //     }
  //   }

  //   return nextTime;
  // };
  const handleEventDrop = useCallback(
    (info: any) => {
      const { event } = info;

      const movedEventId = event.id;
      const newStart = dayjs(event.start);
      const newEnd = dayjs(event.end);
      const newDate = newStart.format("YYYY-MM-DD");

      const oldEvent = visitEvents.find((e) => e.id === movedEventId);
      if (!oldEvent) return;

      const oldStart = dayjs(oldEvent.start);
      const oldDate = oldStart.format("YYYY-MM-DD");

      const updatedMovedEvent: FullCalendarEvent = {
        ...oldEvent,
        start: newStart.toISOString(),
        end: newEnd.toISOString(),
      };

      // Events to keep unchanged
      const otherEvents = visitEvents.filter((e) => e.id !== movedEventId);

      // Adjust OLD day events (move up after removed event)
      const oldDayEvents = otherEvents
        .filter((e) => dayjs(e.start).format("YYYY-MM-DD") === oldDate)
        .sort((a, b) => dayjs(a.start).diff(dayjs(b.start)));

      const adjustedOldDay: FullCalendarEvent[] = [];
      let oldCursor = dayjs(`${oldDate}T07:00:00`);

      for (const event of oldDayEvents) {
        if (
          dayjs(event.start).isSame(oldStart) ||
          dayjs(event.start).isAfter(oldStart)
        ) {
          const duration = dayjs(event.end).diff(dayjs(event.start), "minute");
          const newStart = oldCursor;
          const newEnd = newStart.add(duration, "minute");
          adjustedOldDay.push({
            ...event,
            start: newStart.toISOString(),
            end: newEnd.toISOString(),
          });
          oldCursor = newEnd;
        } else {
          adjustedOldDay.push(event);
          oldCursor = dayjs(event.end);
        }
      }

      // Adjust NEW day events (move down if after dragged time)
      const newDayEvents = otherEvents
        .filter((e) => dayjs(e.start).format("YYYY-MM-DD") === newDate)
        .sort((a, b) => dayjs(a.start).diff(dayjs(b.start)));

      const adjustedNewDay: FullCalendarEvent[] = [];
      let newCursor = newEnd; // Start shifting from end of dropped event
      const inserted = false;

      for (const event of newDayEvents) {
        if (!inserted && dayjs(event.start).isAfter(newStart)) {
          const duration = dayjs(event.end).diff(dayjs(event.start), "minute");
          const newStartTime = newCursor;
          const newEndTime = newStartTime.add(duration, "minute");
          adjustedNewDay.push({
            ...event,
            start: newStartTime.toISOString(),
            end: newEndTime.toISOString(),
          });
          newCursor = newEndTime;
        } else {
          adjustedNewDay.push(event);
          if (dayjs(event.end).isAfter(newCursor)) {
            newCursor = dayjs(event.end);
          }
        }
      }

      // Combine all updated events
      const untouchedEvents = otherEvents.filter(
        (e) =>
          dayjs(e.start).format("YYYY-MM-DD") !== oldDate &&
          dayjs(e.start).format("YYYY-MM-DD") !== newDate
      );

      const finalEvents = [
        ...untouchedEvents,
        ...adjustedOldDay,
        updatedMovedEvent,
        ...adjustedNewDay,
      ].sort((a, b) => dayjs(a.start).diff(dayjs(b.start)));

      setVisitEvents(finalEvents);
    },
    [visitEvents]
  );

  const autoSchedule = useCallback(async () => {
    if (unscheduledTasks.length === 0) {
      return;
    }

    setIsAutoScheduling(true);

    const newEvents: FullCalendarEvent[] = [];
    const tasks = [...unscheduledTasks];

    const startOfNextWeek = dayjs()
      .startOf("week")
      .add(1, "week")
      .add(1, "day"); // Monday
    const days = Array.from({ length: 5 }, (_, i) =>
      startOfNextWeek.add(i, "day")
    );

    const totalTasks = tasks.length;
    const basePerDay = Math.floor(totalTasks / 5);
    const extra = totalTasks % 5;

    const tasksPerDay = Array(5)
      .fill(basePerDay)
      .map((count, i) => count + (i < extra ? 1 : 0));

    let taskIndex = 0;
    const scheduledMinutesPerDay: { [date: string]: number } = {};

    for (let i = 0; i < 5; i++) {
      const day = days[i];
      const dayKey = day.format("YYYY-MM-DD");

      let cursor = day.hour(7).minute(0).second(0);

      for (let j = 0; j < tasksPerDay[i]; j++) {
        const task = tasks[taskIndex];
        const durationMinutes = task.durationHours * 60;
        const end = cursor.add(durationMinutes, "minute");

        newEvents.push({
          id: `${task.id}-scheduled-${Date.now() + taskIndex}`,
          title: task.title,
          start: cursor.toISOString(),
          end: end.toISOString(),
          origin: task.origin,
          editable: true,
          extendedProps: {
            type: "visit",
            location: task.location,
            backgroundColor: "#E8F5E9",
            borderColor: "#4CAF50",
            textColor: "#1B5E20",
            durationMinutes,
            ...task.extendedProps,
          },
        });

        scheduledMinutesPerDay[dayKey] =
          (scheduledMinutesPerDay[dayKey] || 0) + durationMinutes;
        cursor = end;
        taskIndex++;
      }
    }

    setVisitEvents((prev) =>
      [...prev, ...newEvents].sort((a, b) =>
        dayjs(a.start).diff(dayjs(b.start))
      )
    );

    setUnscheduledTasks([]);

    const dailyHours = calculateDailyHours([...visitEvents, ...newEvents]);
    setHoursData(dailyHours);
    setIsAutoScheduling(false);
  }, [unscheduledTasks, visitEvents]);

  const handleDateChange = (date: any) => {
    const calendarApi = calendarRef.current.getApi();
    calendarApi.gotoDate(date.format("YYYY-MM-DD"));
  };

  // if (!crewData || crewData.length === 0) {
  //   return (
  //     <div style={{ padding: "20px", textAlign: "center" }}>
  //       <Typography.Title level={3}>No Crew Data Available</Typography.Title>
  //       <Typography.Text>
  //         Please provide crew data to display the schedule.
  //       </Typography.Text>
  //     </div>
  //   );
  // }

  //hoursData?.reduce((acc, curr) => acc + curr.hours, 0);
  // const startOfNextWeek = dayjs().startOf("week").add(1, "week").add(1, "day"); // next Monday
  // const nextFiveWeekdays = Array.from({ length: 7 }, (_, i) =>
  //   startOfNextWeek.add(i, "day")
  // );

  function getWeekPosition(
    weekStart: string,
    weekEnd: string
  ): "past" | "current" | "future" {
    const startDate = dayjs(weekStart);
    const endDate = dayjs(weekEnd);
    const today = dayjs();

    if (today.isBefore(startDate, "day")) return "future";
    if (today.isAfter(endDate, "day")) return "past";
    return "current";
  }

  const weekPosition = getWeekPosition(
    currentWeekStartEnd.start,
    currentWeekStartEnd.end
  );

  // const totalHours = 73.5;

  return (
    <FullPageLayout loading={driveTimeLoader}>
      <div className="scheduleWorkContainer">
        <Header />
        <div className="scheduleWorkContent">
          <div id="external-events" className="external-event-sidebar">
            <div className="sidebar_wrapper">
              <Typography.Title level={3} style={{ marginBottom: "20px" }}>
                Work to schedule
              </Typography.Title>
              <div
                className="filterSearch"
                style={{
                  display: "flex",
                  gap: "5px",
                  marginBottom: "8px",
                }}
              >
                <Input
                  prefix={<SearchIcon />}
                  placeholder="Search WOs or tasks"
                  allowClear
                  onChange={
                    (e) => e.stopPropagation() // Prevents the event from bubbling up to the FullCalendar
                  }
                />

                <div
                  className="rightBtns"
                  style={{ display: "flex", gap: "5px" }}
                >
                  <Button
                    style={{ height: "36px", width: "36px" }}
                    icon={<SortingIcon />}
                  />
                  <Button
                    style={{ height: "36px", width: "36px" }}
                    icon={<FilterIcon />}
                  />
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  textAlign: "center",
                  marginBottom: "12px",
                  borderBottom: "1px solid #EFF2F3",
                  borderTop: "1px solid #eff2f3",
                  padding: "8px 9px",
                }}
              >
                <Typography.Text type="secondary" className="unscheduled-count">
                  <span style={{ fontWeight: "600" }}>
                    {unscheduledTasks.length}{" "}
                  </span>{" "}
                  Work orders
                </Typography.Text>
                <Typography.Text type="secondary" className="unscheduled-count">
                  <span style={{ fontWeight: "600" }}>
                    {" "}
                    {unscheduledTasks.length}{" "}
                  </span>{" "}
                  tasks
                </Typography.Text>
              </div>

              {unscheduledTasks.length === 0 ? (
                <div className="schedulerEmpty">
                  <EmptyCard
                    image="/images/empty-icons/no-archived.svg"
                    title="No work orders or tasks for this crew"
                    description="Work orders appear here when the crew is assigned to a contract.."
                    imageHeight={66}
                  />
                </div>
              ) : (
                unscheduledTasks.map((task) => (
                  <Card
                    key={task.id}
                    className="fc-event-sidebar"
                    data-id={task.id}
                    draggable="true"
                  >
                    <Typography.Text
                      strong
                      style={{
                        display: "block",
                        paddingTop: "10px",
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#151718",
                        lineHeight: "24px",
                        marginBottom: "8px",
                      }}
                    >
                      {task.title}
                    </Typography.Text>
                    <div className="task-details">
                      <Typography.Text
                        type="secondary"
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          display: "block",
                          color: "#070707",
                        }}
                      >
                        {task.location}
                      </Typography.Text>
                      <Tag
                        className="default-tag"
                        style={{
                          fontSize: "12px",
                          color: "#151718",
                          backgroundColor: "#E7F9F3",
                          border: "1px solid #E7F9F3",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          borderRadius: "10px",
                        }}
                      >
                        <Image
                          src="/images/potential-deals/time-icon.svg"
                          width={12}
                          preview={false}
                        />
                        {task.durationHours}
                        {task.durationHours === 1 ? "h" : "h"}{" "}
                      </Tag>
                    </div>
                  </Card>
                ))
              )}
              {unscheduledTasks.length > 0 && (
                <div className="buttonWrapper">
                  <Button onClick={autoSchedule} loading={isAutoScheduling}>
                    {isAutoScheduling ? "Scheduling..." : "Auto schedule"}
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className="fullcalendar-wrapper">
            <div
              className="scheduleHeader calendarHeader"
              style={{ padding: "12px 12px 4px" }}
            >
              <div className="leftHeader">
                <DatePicker
                  onChange={handleDateChange}
                  format={"ddd, MMM DD"}
                  allowClear={false}
                  suffixIcon={<CalendarIcon />}
                />
                <div className="dateWrapper">
                  <Button
                    icon={<DateNavigatorLeft />}
                    type="text"
                    className="only_icon previous_btn"
                    onClick={() => calendarRef.current.getApi().prev()}
                  />
                  <div className="datePickerWrapper">
                    <span className="date">{currentWeekRange}</span>
                  </div>
                  <Button
                    icon={<DateNavigatorRight />}
                    type="text"
                    className="only_icon next_btn"
                    onClick={() => calendarRef.current.getApi().next()}
                  />
                </div>

                {weekPosition === "current" && (
                  <>
                    <Tag className="current_week">Current week</Tag>

                    <div className="totalHours">
                      Total hours: <span>160/160</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="timeGridWeek"
              firstDay={1}
              headerToolbar={false}
              dayHeaderContent={(arg: any) => (
                <CustomDayHeader
                  date={arg.date}
                  events={calendarEvents}
                  hoursData={hoursData}
                  crewData={crewData}
                />
              )}
              slotMinTime="07:00:00"
              slotMaxTime="23:00:00"
              slotDuration="00:30:00"
              slotLabelInterval="01:00"
              expandRows={true}
              events={calendarEvents}
              eventContent={(e) => <RenderEventContent eventInfo={e} />}
              eventReceive={handleEventReceive}
              droppable={true}
              allDaySlot={false}
              editable={true}
              eventDrop={handleEventDrop}
              height="auto"
              contentHeight="auto"
              eventMinWidth={150}
              aspectRatio={1.8}
              dayHeaderClassNames="custom-day-header"
              dayCellClassNames="custom-day-cell"
              datesSet={handleDatesSet}
              // eventOverlap={false}
              slotEventOverlap={false}
            />
          </div>
        </div>
      </div>
    </FullPageLayout>
  );
}
